import { useEffect, useMemo, useRef, useState } from "react";
import { displayModel } from "@/lib/brand";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Music2, BookOpenText, FileText, Loader2, AlertTriangle,
  Headphones, Copy, ExternalLink, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** mm:ss formatter that gracefully handles missing/null durations. */
function fmtDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Friendly age string for "2 hours ago" style display. */
function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch (e) { void e; return null; }
}

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-50">
      <div className="absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-fuchsia-600/20 blur-3xl" />
      <div className="absolute right-[-200px] top-40 h-[520px] w-[520px] rounded-full bg-sky-500/20 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-emerald-500/10 blur-3xl" />
    </div>
  );
}

function BrandRow() {
  return (
    <Link to="/" data-testid="viewer-home-link" className="group flex items-center gap-3 transition hover:opacity-90">
      <img
        src="/brand/elpm-logo.png"
        alt="ELPM Studio Pro"
        data-testid="brand-wordmark-share"
        className="h-10 w-auto select-none drop-shadow-[0_2px_14px_rgba(232,121,249,0.18)] md:h-12"
        draggable="false"
      />
    </Link>
  );
}

function ErrorState({ message }) {
  return (
    <div data-testid="viewer-error" className="grid place-items-center py-24 text-center">
      <AlertTriangle className="mb-3 h-10 w-10 text-amber-300" />
      <p className="font-display text-xl font-bold text-white">Song not found</p>
      <p className="mt-1 max-w-md text-sm text-white/55">{message}</p>
      <Link
        to="/"
        data-testid="viewer-back-cta"
        className="mt-5 rounded-2xl bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
      >
        Open ELPM Studio Pro
      </Link>
    </div>
  );
}

function LoadingState() {
  return (
    <div data-testid="viewer-loading" className="grid place-items-center py-24 text-center">
      <Loader2 className="mb-3 h-10 w-10 animate-spin text-fuchsia-300" />
      <p className="font-display text-lg text-white/70">Loading song…</p>
    </div>
  );
}

/* Simple visualizer: 60 random-stable bars that wiggle while audio is playing.
 * Cheap, attractive, no Web Audio analyser allocations required for an embed page. */
function FauxVisualizer({ playing }) {
  const seeds = useMemo(() => Array.from({ length: 60 }, () => 0.3 + Math.random() * 0.7), []);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => setTick((t) => t + 1), 90);
    return () => clearInterval(iv);
  }, [playing]);
  return (
    <div className="flex h-24 items-end gap-[3px] rounded-2xl border border-white/10 bg-black/40 px-3 py-2" aria-hidden="true">
      {seeds.map((seed, i) => {
        const oscillation = playing ? (0.55 + 0.45 * Math.abs(Math.sin((i + tick) * 0.42))) : 0.35;
        const h = Math.max(8, seed * oscillation * 100);
        const color = i % 13 === 0 ? "bg-rose-400" : i % 7 === 0 ? "bg-amber-300" : "bg-fuchsia-400/85";
        return <span key={`viz-${i}`} className={`flex-1 rounded-full ${color}`} style={{ height: `${h}%`, opacity: playing ? 1 : 0.55, transition: "height 90ms" }} />;
      })}
    </div>
  );
}

function ShareCopyBar({ shareUrl }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied", { description: "Share it anywhere." });
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      void e;
      toast.error("Could not copy — please copy manually.");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-black/30 px-3 py-2">
      <input
        data-testid="viewer-share-url"
        readOnly
        value={shareUrl}
        className="min-w-[180px] flex-1 truncate bg-transparent font-mono text-[12px] text-white/80 outline-none"
      />
      <Button
        data-testid="viewer-copy-btn"
        onClick={onCopy}
        size="sm"
        variant="secondary"
        className="rounded-xl border border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.12]"
      >
        <Copy className="mr-1.5 h-3.5 w-3.5" />{copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

export default function SongViewer() {
  const { id } = useParams();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const playPostedRef = useRef(false);

  const shareUrl = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}/song/${id}` : ""), [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/song/share/${id}`)
      .then((res) => { if (!cancelled) setSong(res.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(status === 404 ? "This share link doesn't exist or was removed." : (e.message || "Failed to load this song"));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  // Fire a single play-count POST after the user actually starts the audio (privacy-friendly).
  const onAudioPlay = () => {
    setPlaying(true);
    if (playPostedRef.current) return;
    playPostedRef.current = true;
    axios.post(`${API}/song/share/${id}/play`).catch(() => { /* analytics-only — fine to drop */ });
  };
  const onAudioPause = () => setPlaying(false);
  const onAudioEnded = () => setPlaying(false);

  // iter-69 — detect video shares so we render <video> instead of <audio>.
  // Prefer the explicit `media_type` field (set by the backend at create
  // time); fall back to a URL-ext sniff so historic shares display
  // correctly if a video URL ever slipped through pre-iter-69.
  const isVideoShare = song?.media_type === "video"
    || /\.(mp4|mov|webm)(\?|$)/i.test(song?.audio_url || "");

  const durLabel = fmtDuration(song?.duration);
  const ageLabel = song?.created_at ? timeAgo(song.created_at) : null;

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#070914] font-body text-white">
      {/* React 19 hoists these to <head>. Good enough for crawler / social previews
          when the page is rendered (modern OG-aware crawlers run JS). */}
      <title>{song?.title ? `${song.title} · ELPM Studio Pro` : "ELPM Studio Pro"}</title>
      <meta name="description" content={song?.prompt?.slice(0, 240) || "An AI-generated song from ELPM Studio Pro."} />
      <meta property="og:title" content={song?.title || "ELPM Studio Pro"} />
      <meta property="og:description" content={song?.prompt?.slice(0, 240) || (isVideoShare ? "AI-generated music video · made with ELPM Studio Pro" : "AI-generated song · made with ELPM Studio Pro")} />
      <meta property="og:type" content={isVideoShare ? "video.other" : "music.song"} />
      {song?.audio_url && <meta property={isVideoShare ? "og:video" : "og:audio"} content={song.audio_url} />}

      <AmbientBackdrop />

      <div className="relative z-10 mx-auto max-w-5xl p-4 md:p-8">
        <header className="mb-8 flex items-center justify-between gap-4">
          <BrandRow />
          <Link
            to="/"
            data-testid="viewer-open-studio-cta"
            className="hidden rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/[0.12] sm:block"
          >
            Open the studio →
          </Link>
        </header>

        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} />}

        {!loading && song && (
          <motion.main
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="viewer-main"
            className="grid gap-6 lg:grid-cols-[1fr_320px]"
          >
            {/* MAIN COLUMN — visualizer + audio + lyrics */}
            <section className="space-y-5">
              <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl backdrop-blur-xl">
                <div className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-300">
                  <Sparkles className="h-3.5 w-3.5" />AI · Generated · {isVideoShare ? "Music Video" : "Song"}
                </div>
                <h1 data-testid="viewer-title" className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                  {song.title || (isVideoShare ? "Untitled music video" : "Untitled song")}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-white/55">
                  {song.owner_alias && <span data-testid="viewer-owner">by {song.owner_alias}</span>}
                  {song.model && <span data-testid="viewer-model">· {displayModel(song.model)}</span>}
                  {durLabel && <span>· {durLabel}</span>}
                  {ageLabel && <span>· {ageLabel}</span>}
                  <span data-testid="viewer-plays" className="ml-auto rounded-full bg-white/[0.06] px-2 py-0.5">
                    {Number(song.plays || 0).toLocaleString()} play{song.plays === 1 ? "" : "s"}
                  </span>
                </div>

                {/* iter-69 — music-video shares render an inline <video> in
                    place of the audio visualizer; audio shares keep the
                    cosmetic FauxVisualizer + <audio> controls. The play
                    event handlers are identical so the analytics counter
                    behaves the same way. */}
                {isVideoShare ? (
                  <video
                    ref={audioRef}
                    data-testid="viewer-video"
                    src={song.audio_url}
                    controls
                    playsInline
                    preload="metadata"
                    onPlay={onAudioPlay}
                    onPause={onAudioPause}
                    onEnded={onAudioEnded}
                    className="mt-5 aspect-video w-full rounded-2xl bg-black ring-1 ring-fuchsia-400/30"
                  >
                    Your browser does not support inline video playback.
                  </video>
                ) : (
                  <>
                    <div className="mt-5">
                      <FauxVisualizer playing={playing} />
                    </div>
                    <audio
                      ref={audioRef}
                      data-testid="viewer-audio"
                      src={song.audio_url}
                      controls
                      preload="metadata"
                      onPlay={onAudioPlay}
                      onPause={onAudioPause}
                      onEnded={onAudioEnded}
                      className="mt-4 w-full"
                    />
                  </>
                )}

                <div className="mt-4">
                  <ShareCopyBar shareUrl={shareUrl} />
                </div>
              </div>

              {song.lyrics?.trim() && (
                <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
                  <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-300">
                    <BookOpenText className="h-3.5 w-3.5" />Lyrics
                  </div>
                  <pre data-testid="viewer-lyrics" className="whitespace-pre-wrap break-words font-body text-[15px] leading-relaxed text-white/85">
                    {song.lyrics}
                  </pre>
                </div>
              )}
            </section>

            {/* RIGHT RAIL — prompt + CTA */}
            <aside className="space-y-4">
              {song.prompt?.trim() && (
                <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
                  <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-white/55">
                    <FileText className="h-3.5 w-3.5" />Prompt
                  </div>
                  <p data-testid="viewer-prompt" className="text-[13px] leading-relaxed text-white/80">
                    {song.prompt}
                  </p>
                </div>
              )}

              <div className="rounded-3xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/15 via-rose-500/10 to-amber-400/10 p-5 backdrop-blur-xl">
                <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-fuchsia-200">
                  <Headphones className="h-3.5 w-3.5" />Make · your · own
                </div>
                <p className="text-sm leading-6 text-white/85">
                  Make AI-generated songs, clone voices with consent, master tracks, and arrange a 64-channel mix — right in your browser.
                </p>
                <Link
                  to="/"
                  data-testid="viewer-cta"
                  className="mt-3 inline-flex w-full items-center justify-center rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  <Music2 className="mr-2 h-4 w-4" />Open the studio
                  <ExternalLink className="ml-2 h-3.5 w-3.5 opacity-70" />
                </Link>
              </div>
            </aside>
          </motion.main>
        )}

        <footer className="mt-10 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-white/45">
          <span>ELPM · Studio · Pro</span>
          <Link to="/" className="hover:text-white/70">elpm.studio</Link>
        </footer>
      </div>
    </div>
  );
}
